import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/button.tsx";
import { type DataColumn, DataTable } from "../../components/shared/DataTable.tsx";
import { StatusBadge } from "../../components/shared/StatusBadge.tsx";
import { apiRequest } from "../../lib/http.ts";

interface Rule {
  id: string;
  store: string;
  affiliateId: string;
  priority: number;
  active: boolean;
  urlTemplate?: string;
}

const COLUMNS: DataColumn<Rule>[] = [
  { key: "store", label: "Marketplace" },
  { key: "affiliateId", label: "Identificador de afiliado" },
  { key: "priority", label: "Prioridade" },
  {
    key: "active",
    label: "Status",
    render: (rule) => (
      <StatusBadge
        status={rule.active ? "active" : "inactive"}
        customLabel={rule.active ? "Ativa" : "Inativa"}
      />
    ),
  },
  { key: "urlTemplate", label: "Template de URL" },
];

export function RulesPage() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newStore, setNewStore] = useState("amazon");
  const [newAffiliateId, setNewAffiliateId] = useState("");
  const [newPriority, setNewPriority] = useState(100);
  const [newUrlTemplate, setNewUrlTemplate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiRequest<{ items: Rule[] }>("/api/v1/affiliate-rules");
      setRules(result.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar as regras.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => void load()} disabled={loading}>
          Atualizar
        </Button>
      </div>
      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setCreating((v) => !v)}
        >
          {creating ? "Cancelar" : "Nova regra"}
        </Button>
      </div>

      {creating && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-3 font-medium">Nova regra de afiliado</h3>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Marketplace</label>
              <select
                value={newStore}
                onChange={(e) => setNewStore(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="amazon">Amazon</option>
                <option value="shopee">Shopee</option>
                <option value="mercadolivre">Mercado Livre</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Identificador de afiliado</label>
              <input
                value={newAffiliateId}
                onChange={(e) => setNewAffiliateId(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Prioridade</label>
              <input
                type="number"
                value={newPriority}
                onChange={(e) => setNewPriority(Number(e.target.value))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Template de URL (opcional)</label>
              <input
                value={newUrlTemplate}
                onChange={(e) => setNewUrlTemplate(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="{{url}}?tag={{affiliateId}}"
              />
            </div>
            <div className="flex gap-2">
              <Button
                disabled={saving || !newAffiliateId.trim()}
                onClick={async () => {
                  setSaving(true);
                  setError(null);
                  try {
                    await apiRequest("/api/v1/affiliate-rules", {
                      method: "POST",
                      body: JSON.stringify({
                        store: newStore,
                        affiliateId: newAffiliateId.trim(),
                        priority: newPriority,
                        urlTemplate: newUrlTemplate.trim(),
                      }),
                    });
                    setCreating(false);
                    setNewAffiliateId("");
                    setNewUrlTemplate("");
                    setNewPriority(100);
                    await load();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Erro ao criar regra.");
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? "Salvando..." : "Criar"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setCreating(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading
        ? (
          <div className="space-y-2" aria-busy="true">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        )
        : (
          <DataTable
            caption="Regras de links de afiliado por marketplace"
            columns={COLUMNS}
            rows={rules}
            rowKey={(rule, index) => String(rule.id ?? index)}
            emptyTitle="Nenhuma regra cadastrada"
            emptyDescription="Regras são criadas pela API. Elas definem como cada marketplace gera o link de afiliado."
          />
        )}
    </div>
  );
}
